class CollectionQuickFilters extends HTMLElement {
  static STORAGE_KEY = 'collection-quick-filter';
  static AB_TEST_CLASS = 'ab-test-collection-quick-filters';
  static AB_TEST_WAIT_MS = 2000;

  static TAG_FILTERS = {
    'on-sale': 'on-sale-product',
  };

  static SORT_FILTERS = {
    'whats-new': 'created-descending',
    'best-sellers': 'best-selling',
  };

  constructor() {
    super();
    this.buttons = this.querySelectorAll('[data-quick-filter]');
    this.countEl = this.querySelector('#ProductCount');
    this.bindEvents();
    this.migrateLegacyOnSaleUrl();
    this.migrateLegacyWhatsNewUrl();
    this.initCollectionTabs();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bootstrap(), { once: true });
    } else {
      this.bootstrap();
    }

    document.addEventListener('collection:reloaded', this.onCollectionReloaded.bind(this));
    window.addEventListener('popstate', this.onCollectionReloaded.bind(this));
  }

  bindEvents() {
    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.onFilterClick(button.dataset.quickFilter);
      });
    });
  }

  async bootstrap() {
    const buttonsVisible = await this.resolveButtonsVisibility();

    if (!buttonsVisible) {
      this.clearStoredState();
      return;
    }

    this.initFilterState();
  }

  requiresAbTestClass() {
    return this.dataset.intelligemsOnly === 'true';
  }

  areButtonsVisible() {
    const buttonsNav = this.querySelector('.collection-quick-filters__buttons');
    if (!buttonsNav) return false;

    if (!buttonsNav.classList.contains('is-ab-hidden')) return true;

    return document.body.classList.contains(CollectionQuickFilters.AB_TEST_CLASS);
  }

  resolveButtonsVisibility() {
    if (!this.requiresAbTestClass()) {
      return Promise.resolve(this.areButtonsVisible());
    }

    if (this.areButtonsVisible()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const finish = (visible) => {
        observer.disconnect();
        clearInterval(interval);
        resolve(visible);
      };

      const observer = new MutationObserver(() => {
        if (this.areButtonsVisible()) finish(true);
      });

      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      const deadline = Date.now() + CollectionQuickFilters.AB_TEST_WAIT_MS;
      const interval = setInterval(() => {
        if (this.areButtonsVisible()) {
          finish(true);
        } else if (Date.now() >= deadline) {
          finish(false);
        }
      }, 50);
    });
  }

  initCollectionTabs() {
    const scroller = document.querySelector('[data-collection-tabs]');
    if (!scroller) return;

    scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
      if (!link.dataset.collectionUrl) {
        link.dataset.collectionUrl = link.href.split('?')[0];
      }
    });
  }

  initFilterState() {
    const urlFilter = this.getActiveFilter();

    if (urlFilter) {
      this.saveFilter(urlFilter);
      this.syncFromUrl();
      this.updateCollectionTabLinks();
      return;
    }

    const stored = this.getStoredFilter();
    if (stored) {
      this.setActiveButton(stored);

      if (CollectionQuickFilters.TAG_FILTERS[stored]) {
        this.applyTagFilter(stored, false);
      } else if (CollectionQuickFilters.SORT_FILTERS[stored]) {
        this.applySort(stored, false);
      }
    } else {
      this.syncFromUrl();
    }

    this.updateCollectionTabLinks();
  }

  clearStoredState() {
    const wasSetByQuickFilter = !!sessionStorage.getItem(CollectionQuickFilters.STORAGE_KEY);
    sessionStorage.removeItem(CollectionQuickFilters.STORAGE_KEY);
    this.resetCollectionTabLinks();

    // Only remove sort_by/tag from URL if it was originally set by a quick filter click.
    // External links (e.g. ?sort_by=best-selling from a homepage button) must not be cleared.
    if (wasSetByQuickFilter && this.getActiveFilter()) {
      this.clearQuickFilterFromUrl();
    }
  }

  clearQuickFilterFromUrl() {
    const params = new URLSearchParams(window.location.search);
    params.delete('filter.p.tag');
    params.delete('sort_by');
    params.delete('quick_filter');

    const search = params.toString();

    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(search, null, true);
      return;
    }

    history.replaceState({ searchParams: search }, '', search ? `${window.location.pathname}?${search}` : window.location.pathname);
  }

  migrateLegacyWhatsNewUrl() {
    const params = new URLSearchParams(window.location.search);
    const tags = params.getAll('filter.p.tag');

    if (!tags.includes('whats-new')) return;

    params.delete('filter.p.tag');
    params.set('sort_by', CollectionQuickFilters.SORT_FILTERS['whats-new']);
    history.replaceState({ searchParams: params.toString() }, '', `${window.location.pathname}?${params.toString()}`);
  }

  migrateLegacyOnSaleUrl() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('quick_filter')) return;

    params.delete('quick_filter');
    params.set('filter.p.tag', CollectionQuickFilters.TAG_FILTERS['on-sale']);
    history.replaceState({ searchParams: params.toString() }, '', `${window.location.pathname}?${params.toString()}`);
  }

  onFilterClick(filter) {
    if (!this.areButtonsVisible()) return;

    if (this.getActiveFilter() === filter) {
      this.clearFilter();
      return;
    }

    if (CollectionQuickFilters.TAG_FILTERS[filter]) {
      this.applyTagFilter(filter);
      return;
    }

    if (CollectionQuickFilters.SORT_FILTERS[filter]) {
      this.applySort(filter);
    }
  }

  applyTagFilter(filter, persist = true) {
    const tag = CollectionQuickFilters.TAG_FILTERS[filter];
    if (!tag || typeof FacetFiltersForm === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    params.delete('sort_by');
    params.delete('filter.p.tag');
    params.set('filter.p.tag', tag);

    if (persist) {
      this.saveFilter(filter);
      this.updateCollectionTabLinks();
    }

    FacetFiltersForm.renderPage(params.toString(), null, true);
  }

  applySort(filter, persist = true) {
    const sortBy = CollectionQuickFilters.SORT_FILTERS[filter];
    if (!sortBy || typeof FacetFiltersForm === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    params.delete('filter.p.tag');
    params.set('sort_by', sortBy);

    if (persist) {
      this.saveFilter(filter);
      this.updateCollectionTabLinks();
    }

    FacetFiltersForm.renderPage(params.toString(), null, true);
  }

  clearFilter() {
    if (typeof FacetFiltersForm === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    params.delete('filter.p.tag');
    params.delete('sort_by');
    params.delete('quick_filter');

    this.saveFilter(null);
    this.updateCollectionTabLinks();

    FacetFiltersForm.renderPage(params.toString(), null, true);
  }

  getActiveFilter() {
    const params = new URLSearchParams(window.location.search);
    const tags = params.getAll('filter.p.tag');

    if (tags.includes(CollectionQuickFilters.TAG_FILTERS['on-sale'])) return 'on-sale';

    const sortBy = params.get('sort_by');
    if (sortBy === CollectionQuickFilters.SORT_FILTERS['whats-new']) return 'whats-new';
    if (sortBy === CollectionQuickFilters.SORT_FILTERS['best-sellers']) return 'best-sellers';

    return null;
  }

  buildSearchParams(filter) {
    const params = new URLSearchParams();

    if (CollectionQuickFilters.TAG_FILTERS[filter]) {
      params.set('filter.p.tag', CollectionQuickFilters.TAG_FILTERS[filter]);
    } else if (CollectionQuickFilters.SORT_FILTERS[filter]) {
      params.set('sort_by', CollectionQuickFilters.SORT_FILTERS[filter]);
    }

    return params;
  }

  buildCollectionUrl(baseUrl, filter) {
    const url = new URL(baseUrl, window.location.origin);
    const params = this.buildSearchParams(filter);
    url.search = params.toString();
    return url.toString();
  }

  saveFilter(filter) {
    if (!this.areButtonsVisible()) {
      sessionStorage.removeItem(CollectionQuickFilters.STORAGE_KEY);
      return;
    }

    if (filter) {
      sessionStorage.setItem(CollectionQuickFilters.STORAGE_KEY, filter);
    } else {
      sessionStorage.removeItem(CollectionQuickFilters.STORAGE_KEY);
    }
  }

  getStoredFilter() {
    return sessionStorage.getItem(CollectionQuickFilters.STORAGE_KEY);
  }

  resetCollectionTabLinks() {
    const scroller = document.querySelector('[data-collection-tabs]');
    if (!scroller) return;

    scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
      const baseUrl = link.dataset.collectionUrl || link.href.split('?')[0];
      link.href = baseUrl;
    });
  }

  updateCollectionTabLinks() {
    const scroller = document.querySelector('[data-collection-tabs]');
    if (!scroller) return;

    const getOverlaySort = () => {
      if (typeof CollectionSortOverlay !== 'undefined') {
        return CollectionSortOverlay.getActiveSortForTabs();
      }

      return sessionStorage.getItem('collection-sort-overlay-sort');
    };

    if (!this.areButtonsVisible()) {
      const overlaySort = getOverlaySort();

      scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
        const baseUrl = link.dataset.collectionUrl || link.href.split('?')[0];

        if (overlaySort) {
          const url = new URL(baseUrl, window.location.origin);
          url.searchParams.set('sort_by', overlaySort);
          link.href = url.toString();
        } else {
          link.href = baseUrl;
        }
      });
      return;
    }

    const stored = this.getStoredFilter();

    scroller.querySelectorAll('.collection-tabs__item').forEach((link) => {
      const baseUrl = link.dataset.collectionUrl || link.href.split('?')[0];

      if (stored) {
        link.href = this.buildCollectionUrl(baseUrl, stored);
        return;
      }

      const overlaySort = getOverlaySort();
      if (overlaySort) {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('sort_by', overlaySort);
        link.href = url.toString();
        return;
      }

      link.href = baseUrl;
    });
  }

  syncFromUrl() {
    if (!this.areButtonsVisible()) {
      this.clearStoredState();
      return;
    }

    const active = this.getActiveFilter();
    this.setActiveButton(active);
    this.saveFilter(active);
    this.updateCollectionTabLinks();
  }

  onCollectionReloaded() {
    requestAnimationFrame(() => this.syncFromUrl());
  }

  setActiveButton(active) {
    this.buttons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.quickFilter === active);
    });
  }
}

customElements.define('collection-quick-filters', CollectionQuickFilters);
